import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sun, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const IMG =
  "https://images.unsplash.com/photo-1516117172878-fd2c41f4a759?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzbW9reSUyMG1vdW50YWlucyUyMHN1bnJpc2UlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzOTA0NjQ2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA] font-body">
      <div className="hidden lg:block relative grain">
        <img src={IMG} alt="Sunrise" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Sun className="h-10 w-10 text-amber-400" />
          <h2 className="mt-5 font-display text-4xl font-bold leading-tight">
            {'"The light shines in the darkness."'}
          </h2>
          <p className="mt-2 text-slate-300">{"John 1:5 — Manage your ministry's light online."}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-700 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="mt-8">
            <img src="/logo.png" alt="Tennessee Web & Design" className="h-12 w-auto" />
          </div>
          <h1 className="mt-8 font-display text-3xl font-bold text-slate-900 tracking-tight">Welcome back</h1>
          <p className="mt-2 text-slate-600">Sign in to manage your church website.</p>

          <form onSubmit={submit} data-testid="login-form" className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="auth-input" placeholder="you@church.org" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-medium text-amber-700 hover:underline">Forgot?</Link>
              </div>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="auth-input" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold py-3.5 transition-colors duration-200">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Need an account?{" "}
            <a href="/#contact" data-testid="go-contact-link" className="font-semibold text-amber-700 hover:underline">Contact us to get started</a>
          </p>
        </div>
      </div>

      <style>{`
        .auth-input { width:100%; border:1px solid hsl(214 20% 90%); border-radius:0.6rem; padding:0.75rem 0.9rem; font-size:0.9rem; background:#fff; box-shadow: inset 0 1px 2px rgba(0,0,0,0.03); transition:border-color .2s, box-shadow .2s; }
        .auth-input:focus { outline:none; border-color:hsl(32 95% 44%); box-shadow:0 0 0 3px hsl(32 95% 44% / 0.15); }
      `}</style>
    </div>
  );
}
